import { HideImage } from '@mui/icons-material'
import { Button, Dialog, DialogContent, Grid } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInComboPhotoDto, vehicleCheckInComboPhotoValidationSchema } from 'dtos'
import { Formik } from 'formik'
import {
  CheckInPhotoBrowseTakeRow,
  CheckInPhotoDialogNavProps,
  CheckInPhotoDialogTitleRow,
  CheckInPhotoPreview,
  CheckInPhotoStackedNavButtons,
  useCheckInPhotoSubmitIntent,
  useLifecycleDocumentPreview
} from './checkInPhotoDialogCommon'

interface ComboPhotoDialogProps {
  initialValues: VehicleCheckInComboPhotoDto
  isLoading: boolean
  nav?: CheckInPhotoDialogNavProps
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleCheckInComboPhotoDto, options?: { goToNext?: boolean }) => void
  open: boolean
  vehicleGuid: string
}

export default function ComboPhotoDialog({
  initialValues,
  isLoading,
  nav,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: ComboPhotoDialogProps) {
  const { onFormSubmit, submitClose, submitThenNext } = useCheckInPhotoSubmitIntent(onSave)
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getVehicleCheckInPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInComboPhotoValidationSchema}
        onSubmit={onFormSubmit}
      >
        {({ isSubmitting, setFieldValue, submitForm, values }) => {
          return (
            <form onSubmit={e => void e.preventDefault()}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <CheckInPhotoDialogTitleRow onClose={onClose} title='Combo Photo Details' />

                  <Grid item xs={12}>
                    <CheckInPhotoPreview
                      alt='Combo'
                      file={values.vehicleCheckInComboPhotoFile}
                      placeholderSrc='/4 Combo.jpg'
                      serverSrc={serverSrc}
                    />
                  </Grid>

                  <CheckInPhotoBrowseTakeRow
                    browseInputId='combo-browse'
                    cameraInputId='combo-camera'
                    disabled={isSubmitting || isLoading}
                    onFileChosen={file => setFieldValue('vehicleCheckInComboPhotoFile', file)}
                  />

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={isSubmitting || isLoading || !Boolean(values.documentGuid)}
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Combo Photo?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || “Delete Uploaded Photo” can only be pressed if a user has uploaded an image. || “Submit” can only be
                      pressed if a user has uploaded an image. Pressing “Submit” will associate the user-uploaded image with the
                      vehicle record and return the user to the Check-In Photos gallery screen. || Pressing “Previous” goes to
                      the previous photo without saving. ||
                    </ExplanationAccordion>
                  </Grid>

                  <CheckInPhotoStackedNavButtons
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    nav={nav}
                    submitClose={submitClose}
                    submitForm={submitForm}
                    submitThenNext={submitThenNext}
                  />
                </Grid>
              </DialogContent>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
